type MetaPixelPayload = Record<string, unknown>

const isBrowser = typeof window !== 'undefined'
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID
const isProd = import.meta.env.MODE === 'production'
const isMetaPixelEnabled = isBrowser && isProd && Boolean(META_PIXEL_ID)

let pixelInitialized = false

const bootstrapMetaPixel = () => {
  if (!isMetaPixelEnabled || window.fbq) return

  // Lightweight version of Meta's recommended snippet
  ;(function (f: any, b, e, v, n?, t?, s?) {
    if (f.fbq) return
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = !0
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = !0
    t.src = v
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
}

export const initMetaPixel = (): void => {
  if (!isMetaPixelEnabled || pixelInitialized) return

  bootstrapMetaPixel()
  window.fbq?.('init', META_PIXEL_ID)
  pixelInitialized = true
}

/**
 * Check if Meta Pixel (fbq) is loaded and available
 */
function isMetaPixelAvailable(): boolean {
  return isMetaPixelEnabled && typeof window.fbq === 'function'
}

/**
 * Track a custom Meta Pixel event
 */
export function trackMetaPixelEvent(eventName: string, payload: MetaPixelPayload = {}): void {
  if (!eventName || !isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('trackCustom', eventName, payload)
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel custom event', { eventName, error })
  }
}

/**
 * Track standard Meta Pixel event: InitiateCheckout
 * Use when user opens deposit modal or starts checkout process
 */
export function trackMetaInitiateCheckout(payload: MetaPixelPayload = {}): void {
  if (!isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('track', 'InitiateCheckout', payload)
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel InitiateCheckout event', { error })
  }
}

/**
 * Track standard Meta Pixel event: Purchase
 * Use when user completes a deposit/purchase
 */
export function trackMetaPurchase(value: number, currency: string = 'USD', payload: MetaPixelPayload = {}): void {
  if (!isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('track', 'Purchase', {
        value,
        currency,
        ...payload,
      })
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel Purchase event', { error })
  }
}

/**
 * Track standard Meta Pixel event: Lead
 * Use when user signs up or provides contact information
 */
export function trackMetaLead(payload: MetaPixelPayload = {}): void {
  if (!isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('track', 'Lead', payload)
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel Lead event', { error })
  }
}

/**
 * Track standard Meta Pixel event: CompleteRegistration
 * Use when user finishes account creation
 */
export function trackMetaCompleteRegistration(payload: MetaPixelPayload = {}): void {
  if (!isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('track', 'CompleteRegistration', payload)
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel CompleteRegistration event', { error })
  }
}

/**
 * Track standard Meta Pixel event: ViewContent
 * Use when user views important content (e.g., case details)
 */
export function trackMetaViewContent(contentName: string, payload: MetaPixelPayload = {}): void {
  if (!isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('track', 'ViewContent', {
        content_name: contentName,
        ...payload,
      })
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel ViewContent event', { error })
  }
}

export function trackMetaPageView(payload: MetaPixelPayload = {}): void {
  if (!isBrowser) {
    return
  }

  initMetaPixel()

  try {
    if (isMetaPixelAvailable()) {
      window.fbq!('track', 'PageView', payload)
    }
  } catch (error) {
    console.warn('Failed to emit Meta Pixel PageView event', { error })
  }
}

